import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { assetUrl, axiosForAPI } from '../helper_functions/utilities'
import { useCallback, useEffect, useRef, useState } from 'react'
import MatchHistory from './MatchHistory'
import MatchHistoryDesktop from './MatchHistoryDesktop'
import LiveGamePanel from './LiveGamePanel'
import SummonerDetailError from '../errors/SummonerDetailError'
import PreferredRole from './PreferredRole'
import ChampionMastery from './ChampionMastery'
import PersonalChampionPerformance from './PersonalChampionPerformance'
import RecentlyPlayedWith from './RecentlyPlayedWith'
import ProfileIconWithFallback from '../shared/ProfileIcon'
import HorizontalScrollArea from '../shared/HorizontalScrollArea'
import axios from 'axios'
import Seo from './Seo'
import { blackButtonSurfaceClass, buttonFocusRingClass, neutralButtonOnBlackSurfaceClass, neutralButtonOnZincSurfaceClass } from '../shared/buttonStyles'

const queueOptions = [
    { label: "ALL", value: 0 },
    { label: "RANKED SOLO", value: 420 },
    { label: "NORMAL", value: 400 },
    { label: "ARAM", value: 450 },
    { label: "SWIFT PLAY", value: 480 },
    { label: "FLEX", value: 440 },
    { label: "CLASH", value: 700 },
    { label: "ULT SPELLBOOK", value: 1400 },
    { label: "ARENA", value: 1700 },
    { label: "NEXUS BLITZ", value: 1300 },
]

const roleOptions = [
    { label: "ALL", value: "" },
    { label: "TOP", value: "TOP", icon: "position-top-light.svg" },
    { label: "JUNGLE", value: "JUNGLE", icon: "position-jungle-light.svg" },
    { label: "MID", value: "MIDDLE", icon: "position-middle-light.svg" },
    { label: "BOTTOM", value: "BOTTOM", icon: "position-bottom-light.svg" },
    { label: "SUPPORT", value: "UTILITY", icon: "position-utility-light.svg" },
]

const championExclusions = new Set([-1, 0])

const isVisibleChampionFilterOption = (champion) => {
    const championId = parseInt(champion?.champion_id, 10)
    const championName = String(champion?.name || '').trim().toLowerCase()
    const championNameId = String(champion?.name_id || '').trim().toLowerCase()

    return !championExclusions.has(championId)
        && championName !== 'unknown'
        && championNameId !== 'unknown'
}
const MATCH_HISTORY_LAYOUT_STORAGE_KEY = 'matchHistoryDesktopHideCardChrome'
const MATCH_FILTERS_COLLAPSED_STORAGE_KEY = 'matchHistoryFiltersCollapsed'
const LIVE_GAME_MAX_CONSECUTIVE_ERRORS = 4
const LIVE_GAME_NO_GAME_TIMEOUT_MS = 3 * 60 * 1000
const APEX_TIERS = new Set(["MASTER", "GRANDMASTER", "CHALLENGER"])
const roleBadgeLabels = {
    jungle: "JUNGLER",
    support: "SUPPORT",
    bottom: "BOTTOM",
    middle: "MID LANER",
    top: "TOP LANER",
}

const SummonerDetail = ({ searchTrigger, globallyUpdateDisplayedRegion, error, setError }) => {
    const params = useParams()
    const displayName = params.displayNameZipped
    const [gameName, tagLine] = displayName.split("-")

    const [summonerData, setSummonerData] = useState({})
    const [summonerOverview, setSummonerOverview] = useState({})
    const [matchHistory, setMatchHistory] = useState([])
    const [preferredRole, setPreferredRole] = useState({})
    const [championMastery, setChampionMastery] = useState([])
    const [personalChampionPerformances, setPersonalChampionPerformances] = useState([])
    const [recentlyPlayedWith, setRecentlyPlayedWith] = useState([])

    const [orderedChampions, setOrderedChampions] = useState([])
    const [liveGameData, setLiveGameData] = useState(null)
    const [liveGameLoading, setLiveGameLoading] = useState(false)
    const [liveGameError, setLiveGameError] = useState("")
    const [liveGameStaleError, setLiveGameStaleError] = useState("")
    const [liveGamePollingStoppedReason, setLiveGamePollingStoppedReason] = useState("")
    const [queueFilter, setQueueFilter] = useState(0)
    const [championFilter, setChampionFilter] = useState(0)
    const [roleFilter, setRoleFilter] = useState("")
    const [championFilterSearch, setChampionFilterSearch] = useState("")
    const [displayedRank, setDisplayedRank] = useState("solo")
    const [observedPeakElos, setObservedPeakElos] = useState([])
    const [showPeakElo, setShowPeakElo] = useState({ solo: false, flex: false })
    const [hideDesktopMatchCardChrome, setHideDesktopMatchCardChrome] = useState(false)
    const [hideMatchFilters, setHideMatchFilters] = useState(false)

    const [matchHistoryLimit, setMatchHistoryLimit] = useState(3)
    const [matchHistoryStatusMessage, setMatchHistoryStatusMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const location = useLocation()
    const navigate = useNavigate()
    const queueFilterRefs = useRef({})
    const championFilterRefs = useRef({})
    const roleFilterRefs = useRef({})
    const summonerRequestRef = useRef({ id: 0, controller: null })
    const liveGameDataRef = useRef(null)
    const liveGamePanelRef = useRef(null)
    const liveGameConsecutiveErrorsRef = useRef(0)
    const liveGameNoGameStartedAtRef = useRef(0)
    const isLiveGameRoute = location.pathname.endsWith('/live')
    const matchHistoryPath = `/summoners/${params.displayRegion}/${displayName}`
    const liveGamePath = `${matchHistoryPath}/live`
    const decodedGameName = decodeURIComponent(gameName || '')
    const decodedTagLine = decodeURIComponent(tagLine || '')
    const seoProfileName = decodedTagLine ? `${decodedGameName}#${decodedTagLine}` : decodedGameName
    const seoRegion = (params.displayRegion || '').toUpperCase()
    const seoTitle = isLiveGameRoute
        ? `${seoProfileName} (${seoRegion}) Live Game | WINRATES.GG™`
        : `${seoProfileName} (${seoRegion}) Stats | WINRATES.GG™`
    const seoDescription = isLiveGameRoute
        ? `Track the live League of Legends® game for ${seoProfileName} on ${seoRegion} with player matchups, runes, and in-game details on WINRATES.GG™.`
        : `View ${seoProfileName} on ${seoRegion} with ranked overview, match history, True Lead gold-equivalent performance graphs, builds, preferences, champion mastery, and performance stats on WINRATES.GG™.`

    const loadMatchArchives = useCallback(async (matches, signal) => {
        return Promise.all((matches || []).map(async (match) => {
            if (!match?.archive_url) {
                return {
                    ...match,
                    archive_unavailable: true,
                    archived_match_json: null,
                }
            }

            const archiveResponse = await fetch(match.archive_url, { signal })
            if (archiveResponse.status === 404 || archiveResponse.status === 410) {
                return {
                    ...match,
                    archive_unavailable: true,
                    archived_match_json: null,
                }
            }
            if (!archiveResponse.ok) {
                throw new Error(`Match archive unavailable for ${match.match_id}.`)
            }

            return {
                ...match,
                archive_unavailable: false,
                archived_match_json: await archiveResponse.json(),
            }
        }))
    }, [])

    useEffect(() => {
        liveGameDataRef.current = liveGameData
    }, [liveGameData])

    useEffect(() => {
        const storedLayoutPreference = localStorage.getItem(MATCH_HISTORY_LAYOUT_STORAGE_KEY)
        const storedFiltersCollapsedPreference = localStorage.getItem(MATCH_FILTERS_COLLAPSED_STORAGE_KEY)

        if (storedLayoutPreference !== null) {
            setHideDesktopMatchCardChrome(storedLayoutPreference === 'true')
        }

        if (storedFiltersCollapsedPreference !== null) {
            setHideMatchFilters(storedFiltersCollapsedPreference === 'true')
        }
    }, [])

    const countdownKey = 'countdownEndTime'
    const [countdown, setCountdown] = useState(null)

    const clearCountdown = useCallback(() => {
        localStorage.removeItem(countdownKey)
        setCountdown(null)
    }, [])

    const startCountdown = useCallback((duration) => {
        const endTime = Date.now() + duration * 1000
        localStorage.setItem(countdownKey, endTime)
        setCountdown(duration)
    }, [])

    useEffect(() => {
        const storedEndTime = localStorage.getItem(countdownKey)
        if (storedEndTime) {
            const remainingTime = Math.floor((parseInt(storedEndTime, 10) - Date.now()) / 1000)
            if (remainingTime > 0) {
                setCountdown(remainingTime)
            } else {
                clearCountdown()
            }
        }
    }, [clearCountdown])

    useEffect(() => {
        if (countdown !== null) {
            const countdownInterval = setInterval(() => {
                setCountdown((prev) => {
                    const newValue = prev - 1
                    if (newValue <= 0) {
                        clearInterval(countdownInterval)
                        clearCountdown()
                        return null
                    }
                    return newValue
                })
            }, 1000)

            return () => clearInterval(countdownInterval)
        }
    }, [countdown, clearCountdown])

    useEffect(() => {
        const selectedQueueKey = queueFilter ? String(queueFilter) : 'all'
        const activeQueueButton = queueFilterRefs.current[selectedQueueKey]

        if (!activeQueueButton) {
            return
        }

        requestAnimationFrame(() => {
            activeQueueButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        })
    }, [queueFilter, matchHistory])

    useEffect(() => {
        const selectedChampionKey = championFilter ? String(championFilter) : 'all'
        const activeChampionButton = championFilterRefs.current[selectedChampionKey]

        if (!activeChampionButton) {
            return
        }

        requestAnimationFrame(() => {
            activeChampionButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        })
    }, [championFilter, championFilterSearch, orderedChampions])

    useEffect(() => {
        const selectedRoleKey = roleFilter || 'all'
        const activeRoleButton = roleFilterRefs.current[selectedRoleKey]

        if (!activeRoleButton) {
            return
        }

        requestAnimationFrame(() => {
            activeRoleButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        })
    }, [roleFilter])

    useEffect(() => {
        if (!isLiveGameRoute || !summonerData?.puuid || !summonerData?.platform_code) {
            return
        }

        let isMounted = true
        let pollingInterval
        liveGameConsecutiveErrorsRef.current = 0
        liveGameNoGameStartedAtRef.current = Date.now()
        setLiveGamePollingStoppedReason("")

        const stopPolling = (reason) => {
            if (pollingInterval) {
                window.clearInterval(pollingInterval)
            }
            if (isMounted) {
                setLiveGamePollingStoppedReason(reason)
            }
        }

        const fetchLiveGame = async ({ silent = false } = {}) => {
            if (liveGameNoGameStartedAtRef.current
                && Date.now() - liveGameNoGameStartedAtRef.current >= LIVE_GAME_NO_GAME_TIMEOUT_MS) {
                stopPolling("Auto-refresh paused after 3 minutes without an active game.")
                return
            }

            if (!silent) {
                setLiveGameLoading(true)
            }

            try {
                const response = await axiosForAPI.get('/live-game/', {
                    params: {
                        platform: summonerData.platform_code,
                        puuid: summonerData.puuid,
                    },
                })

                if (!isMounted) {
                    return
                }

                setLiveGameData(response.data)
                if (response.data?.active && response.data?.game) {
                    liveGameNoGameStartedAtRef.current = 0
                } else if (!liveGameNoGameStartedAtRef.current) {
                    liveGameNoGameStartedAtRef.current = Date.now()
                }
                setLiveGameError("")
                setLiveGameStaleError("")
                setLiveGamePollingStoppedReason("")
                liveGameConsecutiveErrorsRef.current = 0
            } catch (requestError) {
                if (!isMounted) {
                    return
                }

                const errorPayload = requestError?.response?.data || {}
                const message = errorPayload?.message || "Unable to load live game right now."
                const errorKind = errorPayload?.error_kind || ""
                liveGameConsecutiveErrorsRef.current += 1

                if (liveGameDataRef.current?.active && liveGameDataRef.current?.game) {
                    setLiveGameStaleError(message)
                } else {
                    setLiveGameError(message)
                }

                if (errorKind === 'app_error') {
                    stopPolling("Auto-refresh paused due to an application error.")
                } else if (liveGameConsecutiveErrorsRef.current >= LIVE_GAME_MAX_CONSECUTIVE_ERRORS) {
                    stopPolling("Auto-refresh paused after repeated live-game failures.")
                }
            } finally {
                if (isMounted) {
                    setLiveGameLoading(false)
                }
            }
        }

        fetchLiveGame()
        pollingInterval = window.setInterval(() => fetchLiveGame({ silent: true }), 20000)

        return () => {
            isMounted = false
            if (pollingInterval) {
                window.clearInterval(pollingInterval)
            }
        }
    }, [isLiveGameRoute, summonerData?.platform_code, summonerData?.puuid])

    const getSummonerData = useCallback(async (queryLimit = null, update = false, specificQueue = false, specificChampion = false, specificRole = false) => {
        const previousRequest = summonerRequestRef.current
        previousRequest.controller?.abort()
        const requestId = previousRequest.id + 1
        const controller = new AbortController()
        summonerRequestRef.current = { id: requestId, controller }
        const isCurrentRequest = () => summonerRequestRef.current.id === requestId && !controller.signal.aborted

        setLoading(true)
        const displayedRegion = params.displayRegion
        const mapDisplayedToRegionToVerboseRegion = {
            'na': 'americas',
            'br': 'americas',
            'lan': 'americas',
            'las': 'americas',
            'euw': 'europe',
            'eun': 'europe',
            'tr': 'europe',
            'ru': 'europe',
            'kr': 'asia',
            'jp': 'asia',
            'oc': 'asia',
            'sg': 'asia',
            'tw': 'asia',
            'vn': 'asia',
        }
        const newRegion = mapDisplayedToRegionToVerboseRegion[displayedRegion]

        const mapDisplayedRegionToPlatform = {
            'na': 'na1',
            'br': 'br1',
            'lan': 'la1',
            'las': 'la2',
            'euw': 'euw1',
            'eun': 'eun1',
            'tr': 'tr1',
            'ru': 'ru',
            'kr': 'kr',
            'jp': 'jp1',
            'oc': 'oc1',
            'sg': 'sg2',
            'tw': 'tw2',
            'vn': 'vn2',
        }
        const newPlatform = mapDisplayedRegionToPlatform[displayedRegion]

        const signal = controller.signal
        const timeout = setTimeout(() => {
            controller.abort()
        }, 120000)

        try {
            const response = await axiosForAPI.get("/summoner/", {
                params: {
                    region: newRegion,
                    platform: newPlatform,
                    gameName,
                    tagLine,
                    ...(queryLimit && { matches_limit: queryLimit }),
                    ...(update && { update }),
                    ...(specificQueue && { queueId: specificQueue }),
                    ...(specificChampion && { championId: specificChampion }),
                    ...(specificRole && { roleId: specificRole }),
                },
                signal,
            })
            clearTimeout(timeout)
            if (!isCurrentRequest()) return null
            setSummonerData(response.data.summoner)
            setSummonerOverview(response.data.overviews[0])
            setObservedPeakElos(response.data.observed_peak_elos || [])
            const loadedMatches = await loadMatchArchives(response.data.matches, signal)
            if (!isCurrentRequest()) return null
            setMatchHistory(loadedMatches)
            setPreferredRole(response.data.preferred_roles)
            setChampionMastery(response.data.champion_masteries)
            setOrderedChampions(response.data.ordered_champions)
            setPersonalChampionPerformances(response.data.personal_champion_performances)
            setRecentlyPlayedWith(response.data.recently_played_with || [])
            globallyUpdateDisplayedRegion(newPlatform)
            setLoading(false)
            setError(false)

            if (response.status === 206) {
                const retryTime = response.headers.get('x-retry-after')
                startCountdown(parseInt(retryTime))
            }

            return { ...response.data, matches: loadedMatches }
        } catch (requestError) {
            clearTimeout(timeout)
            if (!isCurrentRequest()) return null
            setLoading(false)
            if (axios.isCancel(requestError)) {
                setError({ "message": "Request took excessively long. Please try again momentarily", status_code: 408 })
            } else if (requestError.response) {
                const responseData = requestError.response.data
                const normalizedError = responseData && typeof responseData === 'object'
                    ? responseData
                    : { message: responseData || "An unexpected error occurred while loading this profile." }
                const statusCode = requestError.response.status
                const derivedErrorKind = normalizedError.error_kind || (
                    [502, 503, 504].includes(statusCode)
                        ? 'transient_upstream'
                        : statusCode === 429
                            ? 'riot_rate_limit'
                            : statusCode === 500
                                ? 'app_error'
                                : ''
                )

                setError({
                    ...normalizedError,
                    error_kind: derivedErrorKind,
                    status_code: statusCode,
                })
            } else {
                const transientNetworkCodes = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ECONNREFUSED', 'ENOTFOUND', 'ETIMEDOUT'])
                setError({
                    message: "We could not reach the server right now. Please try again in a moment.",
                    error_kind: transientNetworkCodes.has(requestError.code) ? 'transient_network' : 'app_error',
                    status_code: requestError.code || "UNKNOWN",
                })
            }

            return null
        }
    }, [params.displayRegion, gameName, tagLine, globallyUpdateDisplayedRegion, loadMatchArchives, setError, startCountdown])

    useEffect(() => {
        setSummonerData({})
        setSummonerOverview({})
        setObservedPeakElos([])
        setShowPeakElo({ solo: false, flex: false })
        setMatchHistory([])
        setPreferredRole({})
        setRoleFilter("")
        setChampionMastery([])
        setPersonalChampionPerformances([])
        setRecentlyPlayedWith([])
        setOrderedChampions([])
        setError(false)
        getSummonerData()
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        setLiveGameData(null)
        setLiveGameError("")
        setLiveGameStaleError("")
        setLiveGamePollingStoppedReason("")
        liveGameConsecutiveErrorsRef.current = 0
        liveGameNoGameStartedAtRef.current = 0

        return () => {
            summonerRequestRef.current.controller?.abort()
        }
    }, [getSummonerData, location.pathname, searchTrigger, setError])

    useEffect(() => {
        if (!isLiveGameRoute || typeof window === 'undefined' || window.innerWidth >= 768) {
            return undefined
        }

        const frame = window.requestAnimationFrame(() => {
            liveGamePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })

        return () => window.cancelAnimationFrame(frame)
    }, [isLiveGameRoute])

    function forceUpdatePage() {
        getSummonerData(matchHistoryLimit, true, queueFilter, championFilter, roleFilter)
    }

    function toggleLiveGamePanel() {
        navigate(isLiveGameRoute ? matchHistoryPath : liveGamePath)
    }

    function formatLastUpdateTime(timestamp) {
        const currentTime = new Date()
        const targetTime = new Date(timestamp)
        if (!timestamp || (isNaN(targetTime.getTime()))) return ': Unknown.'

        const difference = currentTime - targetTime
        const seconds = Math.floor(difference / 1000)
        const minute = 60
        const hour = minute * 60
        const day = hour * 24
        const month = day * 30
        const year = day * 365

        if (seconds < minute) {
            return `A Moment Ago`
        } else if (seconds < hour) {
            const minutes = Math.floor(seconds / minute)
            return `${minutes} Minute${minutes !== 1 ? 's' : ''} Ago`
        } else if (seconds < day) {
            const hours = Math.floor(seconds / hour)
            return `${hours} Hour${hours !== 1 ? 's' : ''} Ago`
        } else if (seconds < month) {
            const days = Math.floor(seconds / day)
            return `${days} Day${days !== 1 ? 's' : ''} Ago`
        } else if (seconds < year) {
            const months = Math.floor(seconds / month)
            return `${months} Month${months !== 1 ? 's' : ''} Ago`
        } else {
            const years = Math.floor(seconds / year)
            return `${years} Year${years !== 1 ? 's' : ''} Ago`
        }
    }

    function updateQueueFilterAndGetRecentMatchDetails(queueId, championId) {
        setQueueFilter(queueId)
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        if (!championId) {
            getSummonerData(3, false, queueId, false, roleFilter)
        } else {
            getSummonerData(3, false, queueId, championId, roleFilter)
        }
    }

    function undoQueueFilterAndGetRecentMatchDetails(championId) {
        setQueueFilter(0)
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        if (!championId) {
            getSummonerData(null, false, false, false, roleFilter)
        } else {
            getSummonerData(null, false, false, championId, roleFilter)
        }
    }

    function updateChampionFilterAndGetRecentMatchDetails(queueId, championId) {
        setChampionFilter(championId)
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        if (!queueId) {
            getSummonerData(3, false, false, championId, roleFilter)
        } else {
            getSummonerData(3, false, queueId, championId, roleFilter)
        }
    }

    function undoChampionFilterAndGetRecentMatchDetails(queueId) {
        setChampionFilter(0)
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        if (!queueId) {
            getSummonerData(null, false, false, false, roleFilter)
        } else {
            getSummonerData(null, false, queueId, false, roleFilter)
        }
    }

    function updateRoleFilterAndGetRecentMatchDetails(roleId) {
        setRoleFilter(roleId)
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        getSummonerData(3, false, queueFilter, championFilter, roleId)
    }

    function undoAllFilters() {
        setChampionFilter(0)
        setQueueFilter(0)
        setRoleFilter("")
        setMatchHistoryLimit(3)
        setMatchHistoryStatusMessage('')
        getSummonerData()
    }

    async function loadMoreMatches() {
        const nextLimit = matchHistoryLimit + 3
        const previousCount = matchHistory?.length || 0
        setMatchHistoryLimit(nextLimit)
        setMatchHistoryStatusMessage('Loading more games…')

        const responseData = await getSummonerData(nextLimit, false, queueFilter, championFilter, roleFilter)
        const nextCount = responseData?.matches?.length || 0

        if (nextCount <= previousCount) {
            setMatchHistoryStatusMessage('No new games loaded yet.')
        } else {
            setMatchHistoryStatusMessage('')
        }
    }

    function formatTierIcon(singleSummonerOverview) {
        const capitalizedFirstLetter = singleSummonerOverview?.tier?.charAt(0)?.toUpperCase()
        const remainingLetters = singleSummonerOverview?.tier?.slice(1).toLowerCase()
        return capitalizedFirstLetter + remainingLetters
    }

    if (error) {
        return <SummonerDetailError error={error} countdown={countdown} />
    }

    function getSoloBadgeStyle(tier) {
        if (tier === "CHALLENGER" || tier === "GRANDMASTER" || tier === "MASTER" || tier === "DIAMOND" || tier === "EMERALD") {
            return "mr-3"
        } else {
            return "-ml-3 mr-2"
        }
    }

    function getFlexBadgeStyle(tier) {
        if (tier === "CHALLENGER" || tier === "GRANDMASTER" || tier === "MASTER" || tier === "DIAMOND" || tier === "EMERALD") {
            return "ml-4 mr-3"
        } else {
            return "ml-1 mr-3"
        }
    }

    function getPrimaryRoleBadge(roleData) {
        if (!roleData) {
            return { label: "ROLE UNKNOWN", iconSrc: "" }
        }

        const roleValues = Object.entries(roleBadgeLabels).map(([key, label]) => ({
            key,
            label,
            value: Number(roleData?.[key] || 0),
        }))

        roleValues.sort((a, b) => b.value - a.value)

        if (!roleValues[0] || roleValues[0].value <= 0) {
            return { label: "ROLE UNKNOWN", iconSrc: "" }
        }

        const iconKey = roleValues[0].key === "support" ? "utility" : roleValues[0].key

        return {
            label: roleValues[0].label,
            iconSrc: `${process.env.PUBLIC_URL}/assets/role_icons/position-${iconKey}-light.svg`,
        }
    }

    const primaryRoleBadge = getPrimaryRoleBadge(preferredRole)
    const roleBadgeClassName = "inline-flex self-start items-center gap-1.5 rounded-md border border-cyan-700/30 bg-cyan-900/15 px-2 py-0.5 text-[10px] font-bold tracking-wide text-cyan-200"
    const secondaryActionButtonClassName = `inline-flex h-8 justify-center items-center rounded-md px-2.5 text-sm font-semibold transition-colors ${buttonFocusRingClass}`
    const ladderSelectedButtonClassName = '!border-zinc-700 !bg-cyan-900/45 !text-cyan-200 hover:!border-zinc-700 hover:!bg-cyan-900/45 hover:!text-cyan-200'
    const filterButtonInactiveClass = `inline-flex h-8 items-center justify-center rounded-full px-3 text-[11px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors ${buttonFocusRingClass} ${neutralButtonOnZincSurfaceClass}`
    const filterButtonActiveClass = `inline-flex h-8 items-center justify-center rounded-full px-3 text-[11px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors ${buttonFocusRingClass} ${ladderSelectedButtonClassName}`
    const getFilterButtonClass = (isActive) => (isActive ? filterButtonActiveClass : filterButtonInactiveClass)
    const utilityButtonClass = `${secondaryActionButtonClassName} text-xs ${neutralButtonOnBlackSurfaceClass}`
    const utilityButtonMdClass = `${secondaryActionButtonClassName} text-sm ${neutralButtonOnBlackSurfaceClass}`
    const updateButtonClassName = `${utilityButtonMdClass} ${blackButtonSurfaceClass}`
    const showMoreButtonClass = `h-10 w-full rounded-md px-4 text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors ${buttonFocusRingClass} ${blackButtonSurfaceClass}`
    const profileLiveGameButtonClassName = `inline-flex h-8 items-center justify-center gap-1.5 rounded-md px-2.5 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${blackButtonSurfaceClass} ${
        isLiveGameRoute ? '!border-[#33333a] !text-zinc-50' : ''
    }`
    const liveGameButtonClassName = `inline-flex h-8 items-center justify-center gap-1.5 rounded-md px-2.5 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${blackButtonSurfaceClass}`
    const eloModeToggleClassName = `inline-flex h-5 items-center rounded border border-zinc-800/80 bg-zinc-900/45 px-1.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-zinc-500 transition-colors hover:border-zinc-700 hover:bg-zinc-900/70 hover:text-zinc-300 ${buttonFocusRingClass}`
    const topCardClassName = 'rounded-xl border border-zinc-800 bg-zinc-950 text-white shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)]'
    const topCardBodyClassName = 'p-3 md:pb-4'
    const compactStatLabelClassName = 'text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-300'
    const filterToolbarClassName = 'mb-2 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-white shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)]'
    const filterGroupHeaderClassName = 'flex items-center gap-2'
    const filterSectionLabelClassName = 'text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300'
    const filterSectionCardClassName = 'min-w-0 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 pt-2 pb-1.5'
    const filterSearchInputClassName = 'h-8 w-full rounded-md border border-zinc-800 bg-zinc-950/90 px-2.5 text-[11px] font-semibold text-zinc-200 placeholder:text-[10px] placeholder:font-bold placeholder:uppercase placeholder:tracking-[0.14em] placeholder:text-zinc-500 focus:border-cyan-700/50 focus:outline-none focus:ring-1 focus:ring-cyan-700/40'
    const soloEloData = {
        status: summonerOverview?.solo_queue_status,
        tier: summonerOverview?.solo_tier,
        division: summonerOverview?.solo_division,
        leaguePoints: summonerOverview?.solo_lp ?? 0,
        wins: summonerOverview?.solo_wins ?? 0,
        losses: summonerOverview?.solo_losses ?? 0,
    }
    const flexEloData = {
        status: summonerOverview?.flex_queue_status,
        tier: summonerOverview?.flex_tier,
        division: summonerOverview?.flex_division,
        leaguePoints: summonerOverview?.flex_lp ?? 0,
        wins: summonerOverview?.flex_wins ?? 0,
        losses: summonerOverview?.flex_losses ?? 0,
    }

    function getEloData(queueType) {
        const currentEloData = queueType === "solo" ? soloEloData : flexEloData
        if (!showPeakElo[queueType]) return currentEloData

        const queueName = queueType === "solo" ? "RANKED_SOLO_5x5" : "RANKED_FLEX_SR"
        const peak = observedPeakElos.find((entry) => entry.queue_type === queueName)
        return peak ? {
            status: "RANKED",
            tier: peak.peak_tier,
            division: peak.peak_division,
            leaguePoints: peak.peak_league_points ?? 0,
            wins: null,
            losses: null,
            isPeak: true,
        } : { status: "INVALID", tier: null, division: null, leaguePoints: 0, wins: null, losses: null, isPeak: true }
    }

    function getEloTitle(queueType) {
        return queueType === "solo" ? "SOLO/DUO QUEUE" : "FLEX QUEUE"
    }

    function getEloDisplay(eloData) {
        if (eloData?.status === "INVALID" || eloData?.status === "PARTIAL") {
            return "ELO DATA UNAVAILABLE"
        }

        if (eloData?.status === "UNRANKED") {
            return "UNRANKED"
        }

        if (!eloData?.tier || eloData?.division === "UNRANKED") {
            return "UNRANKED"
        }

        if (APEX_TIERS.has(eloData.tier)) {
            return eloData.tier
        }

        return `${eloData.tier} ${eloData.division}`
    }

    function getTierIconSource(eloData) {
        return process.env.PUBLIC_URL + `/assets/ranked_icons/Rank=${eloData?.tier ? formatTierIcon(eloData) : "Unranked"}.png`
    }

    function getWinRateText(eloData) {
        if (eloData?.isPeak) return '—'
        const wins = eloData?.wins ?? 0
        const losses = eloData?.losses ?? 0
        const totalGames = wins + losses

        if (!totalGames) {
            return "N/A"
        }

        return `${Math.round((wins / totalGames) * 100)}%`
    }

    function blurTappedButton(event) {
        event.currentTarget.blur()
    }

    function renderLoadingSpinner(sizeClassName = "h-7 w-7") {
        return (
            <div
                aria-hidden="true"
                className={`${sizeClassName} animate-spin rounded-full border-2 border-zinc-700/80 border-t-cyan-400/90`}
            />
        )
    }

    function renderCardHeader(title) {
        return (
            <div className="-mx-3 -mt-3 mb-3 border-b border-zinc-800 bg-zinc-900/65 px-3 py-3 text-left">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">{title}</h2>
            </div>
        )
    }

    function renderLoadingCard(title, bodyClassName = "min-h-[120px]", wrapperClassName = "", footerSpacerClassName = "") {
        return (
            <div className={wrapperClassName}>
                <section className={topCardClassName}>
                    <div className={topCardBodyClassName}>
                        {renderCardHeader(title)}
                        <div className={`flex items-center justify-center ${bodyClassName}`}>
                            {renderLoadingSpinner()}
                        </div>
                        {footerSpacerClassName ? <div className={footerSpacerClassName} aria-hidden="true" /> : null}
                    </div>
                </section>
            </div>
        )
    }

    function renderUpdateControl(extraClassName = "") {
        if (loading) {
            return (
                <button
                    type="button"
                    className={`${updateButtonClassName} ${extraClassName}`}
                    aria-busy="true"
                    aria-label="Loading profile"
                    disabled
                >
                    {renderLoadingSpinner("h-3.5 w-3.5")}
                </button>
            )
        }

        if (countdown !== null) {
            return (
                <div className={`inline-flex h-8 justify-center items-center rounded-md px-2 text-xs font-semibold bg-zinc-800 text-zinc-200 border border-zinc-700 ${extraClassName}`}>
                    Update in {countdown}s
                </div>
            )
        }

        return (
            <button type="button" className={`${updateButtonClassName} ${extraClassName}`} onClick={() => forceUpdatePage()}>
                UPDATE
            </button>
        )
    }

    function renderProfileActions(extraClassName = "") {
        return (
            <div className={`grid grid-cols-2 gap-2 ${extraClassName}`}>
                {renderUpdateControl("w-full")}
                <button
                    type="button"
                    className={`${profileLiveGameButtonClassName} w-full`}
                    onClick={toggleLiveGamePanel}
                >
                    <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${isLiveGameRoute ? 'bg-cyan-300' : 'bg-zinc-500'}`} />
                    {isLiveGameRoute ? 'MATCHES' : 'LIVE'}
                </button>
            </div>
        )
    }

    function renderSummaryCard(isMobile = false) {
        const isSkeleton = loading && !summonerData?.game_name
        return (
            <section className={topCardClassName}>
                <div className={`${topCardBodyClassName} flex h-full flex-col`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            {isSkeleton ? (
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                    <div className="h-7 w-44 rounded-md bg-zinc-800/70" />
                                    <div className="h-4 w-24 rounded-md bg-zinc-800/60" />
                                </div>
                            ) : (
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                    <h1 className="truncate text-[clamp(1.2rem,2.2vw,1.65rem)] font-bold tracking-tight text-zinc-100">
                                        {summonerData?.game_name}
                                    </h1>
                                    <div className="text-sm font-semibold text-zinc-300">
                                        #{summonerData?.tag_line}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {isMobile ? (
                        <div className="mt-2 grid grid-cols-[88px_minmax(0,1fr)] items-stretch gap-3 pb-2">
                            <div className="relative flex h-[88px] w-[88px] shrink-0 items-center justify-center self-stretch">
                                {isSkeleton ? (
                                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-zinc-700/80 bg-zinc-900/80">
                                        {renderLoadingSpinner("h-6 w-6")}
                                    </div>
                                ) : (
                                    <>
                                        <ProfileIconWithFallback src={assetUrl(`/profile_icons/${summonerData?.profile_icon_id}.png`)} alt="profile icon" />
                                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-sm border border-zinc-700 bg-zinc-900 px-2 text-sm">
                                            {summonerOverview?.summoner_level ?? '—'}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex h-[88px] min-w-0 flex-1 flex-col justify-between self-stretch py-0.5 pb-1">
                                <div className={roleBadgeClassName}>
                                    {isSkeleton ? (
                                        <span className="h-3.5 w-3.5 rounded-full bg-cyan-400/40" />
                                    ) : primaryRoleBadge.iconSrc ? (
                                        <img alt={`${primaryRoleBadge.label} role icon`} className="h-3.5 w-3.5 opacity-85" src={primaryRoleBadge.iconSrc} />
                                    ) : (
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                                    )}
                                    {isSkeleton ? 'LOADING' : primaryRoleBadge.label}
                                </div>
                                <div>
                                    {renderProfileActions("w-full")}
                                </div>
                                <div className="border-t border-zinc-800 pt-1.5">
                                    {isSkeleton ? (
                                        <div className="h-3.5 w-28 rounded-md bg-zinc-800/60" />
                                    ) : (
                                        <div className="text-[10.5px] italic leading-none tracking-[0.02em] text-zinc-300">
                                            Last updated {formatLastUpdateTime(summonerData?.updated_at)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-2.5 flex items-stretch gap-3">
                            <div className="relative flex h-[92px] w-[92px] shrink-0 items-center justify-center self-stretch">
                                {isSkeleton ? (
                                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-zinc-700/80 bg-zinc-900/80">
                                        {renderLoadingSpinner("h-6 w-6")}
                                    </div>
                                ) : (
                                    <>
                                        <ProfileIconWithFallback src={assetUrl(`/profile_icons/${summonerData?.profile_icon_id}.png`)} alt="profile icon" />
                                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-sm border border-zinc-700 bg-zinc-900 px-2 text-sm">
                                            {summonerOverview?.summoner_level ?? '—'}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex h-[92px] min-w-0 flex-1 flex-col justify-between self-stretch">
                                <div className={roleBadgeClassName}>
                                    {isSkeleton ? (
                                        <span className="h-3.5 w-3.5 rounded-full bg-cyan-400/40" />
                                    ) : primaryRoleBadge.iconSrc ? (
                                        <img alt={`${primaryRoleBadge.label} role icon`} className="h-3.5 w-3.5 opacity-85" src={primaryRoleBadge.iconSrc} />
                                    ) : (
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                                    )}
                                    {isSkeleton ? 'LOADING' : primaryRoleBadge.label}
                                </div>
                                <div>
                                    {renderProfileActions("w-full")}
                                </div>
                                <div className="border-t border-zinc-800/70 pt-2">
                                    {isSkeleton ? (
                                        <div className="h-3.5 w-28 rounded-md bg-zinc-800/60" />
                                    ) : (
                                        <div className="text-[11px] italic tracking-[0.02em] text-zinc-300">
                                            Last updated {formatLastUpdateTime(summonerData?.updated_at)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        )
    }

    function renderRankCard(queueType, isMobile = false) {
        const eloData = getEloData(queueType)
        const isPeak = Boolean(eloData?.isPeak)
        const desktopRankIconClassName = queueType === "solo" ? getSoloBadgeStyle(eloData?.tier) : getFlexBadgeStyle(eloData?.tier)
        const wins = eloData?.wins ?? '—'
        const losses = eloData?.losses ?? '—'

        if (loading) {
            return (
                <section className={`${topCardClassName} relative md:h-full`}>
                    <div className={topCardBodyClassName}>
                        {renderCardHeader(getEloTitle(queueType))}
                        <div className={`flex items-center justify-center ${isMobile ? 'h-[82px]' : 'h-[100px]'}`}>
                            {renderLoadingSpinner(isMobile ? 'h-8 w-8' : 'h-9 w-9')}
                        </div>
                    </div>
                    <button type="button" className={`${eloModeToggleClassName} absolute bottom-3 right-3`} onClick={() => setShowPeakElo((current) => ({ ...current, [queueType]: !current[queueType] }))}>{isPeak ? 'CURRENT' : 'PEAK'}</button>
                </section>
            )
        }

        const icon = isMobile ? (
            <div className="flex w-[92px] shrink-0 items-center justify-center">
                <img
                    className="block h-[82px] w-[82px] shrink-0 object-contain"
                    width="82"
                    height="82"
                    alt="ranked icons"
                    src={getTierIconSource(eloData)}
                />
            </div>
        ) : (
            <div className="flex w-[100px] shrink-0 items-center justify-center">
                <img
                    className={desktopRankIconClassName}
                    width="88"
                    height="88"
                    alt="ranked icons"
                    src={getTierIconSource(eloData)}
                />
            </div>
        )

        const details = (
            <div className="min-w-0 flex-1">
                {isMobile ? (
                    <div className="mt-0.5">
                        <div className="text-[15px] font-bold tracking-tight text-zinc-100">
                            {getEloDisplay(eloData)}
                        </div>
                        <div className="mt-0.5 text-xs font-semibold text-zinc-300">
                            {eloData?.leaguePoints ?? 0} LP
                        </div>
                    </div>
                ) : (
                    <div className="mt-1 text-sm font-bold tracking-tight text-zinc-100">
                        {getEloDisplay(eloData)}
                        <span className="ml-2 text-xs font-semibold text-zinc-300">{eloData?.leaguePoints ?? 0} LP</span>
                    </div>
                )}
                <div className={`${isMobile ? "mt-1.5 gap-4 pt-1.5 text-sm" : "mt-1 gap-3 pt-1 text-xs"} flex items-center border-t border-zinc-800/70`}>
                    <div>
                        <div className={compactStatLabelClassName}>WINRATE</div>
                        <div className={`${isMobile ? "mt-1 text-sm" : "mt-0.5 text-xs"} font-semibold text-zinc-200`}>{getWinRateText(eloData)}</div>
                    </div>
                    <div>
                        <div className={compactStatLabelClassName}>W</div>
                        <div className={`${isMobile ? "mt-1 text-sm" : "mt-0.5 text-xs"} font-semibold text-zinc-200`}>{wins}</div>
                    </div>
                    <div>
                        <div className={compactStatLabelClassName}>L</div>
                        <div className={`${isMobile ? "mt-1 text-sm" : "mt-0.5 text-xs"} font-semibold text-zinc-200`}>{losses}</div>
                    </div>
                </div>
            </div>
        )

        const mobileStats = (
            <div className="mt-1.5 pt-2 text-sm">
                <div className="inline-flex items-center gap-4 border-t border-zinc-800/70 pt-2 text-sm">
                    <div>
                        <div className={compactStatLabelClassName}>WINRATE</div>
                        <div className="mt-1 text-sm font-semibold text-zinc-200">{getWinRateText(eloData)}</div>
                    </div>
                    <div>
                        <div className={compactStatLabelClassName}>W</div>
                        <div className="mt-1 text-sm font-semibold text-zinc-200">{wins}</div>
                    </div>
                    <div>
                        <div className={compactStatLabelClassName}>L</div>
                        <div className="mt-1 text-sm font-semibold text-zinc-200">{losses}</div>
                    </div>
                </div>
            </div>
        )

        return (
            <section className={`${topCardClassName} relative md:h-full`}>
                <div className={topCardBodyClassName}>
                    {renderCardHeader(getEloTitle(queueType))}
                    {isMobile ? (
                        <div className="grid grid-cols-[minmax(0,1fr)_108px_44px] grid-rows-[auto_auto] gap-x-3 gap-y-2">
                            <div className="col-start-1 row-start-1 min-w-0">
                                <div className="mt-0.5">
                                    <div className="text-[15px] font-bold tracking-tight text-zinc-100">
                                        {getEloDisplay(eloData)}
                                    </div>
                                    <div className="mt-0.5 text-xs font-semibold text-zinc-300">
                                        {eloData?.leaguePoints ?? 0} LP
                                    </div>
                                </div>
                            </div>
                            <div className="col-start-2 row-span-2 row-start-1 flex items-center justify-center self-center -ml-16">
                                {icon}
                            </div>
                            <div className="col-start-3 row-start-1 flex items-start justify-end">
                                <button
                                    type="button"
                                    className={`${liveGameButtonClassName} h-5.5 whitespace-nowrap px-1.5 text-[8px] tracking-[0.12em]`}
                                    onClick={() => setDisplayedRank((prevValue) => (prevValue === "solo" ? "flex" : "solo"))}
                                    onPointerUp={blurTappedButton}
                                >
                                    TOGGLE
                                </button>
                            </div>
                            <div className="min-w-0 col-start-1 row-start-2">
                                {mobileStats}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between gap-3">
                            {details}
                            {icon}
                            </div>
                        </>
                    )}
                </div>
                <button type="button" className={`${eloModeToggleClassName} absolute bottom-3 right-3`} onClick={() => setShowPeakElo((current) => ({ ...current, [queueType]: !current[queueType] }))}>{isPeak ? 'CURRENT' : 'PEAK'}</button>
            </section>
        )
    }

    function renderQueueFilterButtons() {
        return queueOptions.map((option) => {
            if (option.value === 0) {
                return (
                    <button
                        ref={(node) => {
                            queueFilterRefs.current.all = node
                        }}
                        key={option.label}
                        type="button"
                        className={getFilterButtonClass(!queueFilter)}
                        onClick={() => undoQueueFilterAndGetRecentMatchDetails(championFilter)}
                        onPointerUp={blurTappedButton}
                    >
                        {option.label}
                    </button>
                )
            }

            return (
                <button
                    ref={(node) => {
                        queueFilterRefs.current[String(option.value)] = node
                    }}
                    key={option.label}
                    type="button"
                    className={getFilterButtonClass(queueFilter === option.value)}
                    onClick={() => updateQueueFilterAndGetRecentMatchDetails(option.value, championFilter)}
                    onPointerUp={blurTappedButton}
                >
                    {option.label}
                </button>
            )
        })
    }

    function renderChampionFilterButtons() {
        const normalizedSearch = championFilterSearch.trim().toLowerCase()

        return [
            <button
                ref={(node) => {
                    championFilterRefs.current.all = node
                }}
                key="all"
                type="button"
                className={getFilterButtonClass(!championFilter)}
                onClick={() => undoChampionFilterAndGetRecentMatchDetails(queueFilter)}
                onPointerUp={blurTappedButton}
            >
                ALL
            </button>,
            ...(orderedChampions?.filter(isVisibleChampionFilterOption).map((champ) => {
                const championId = parseInt(champ.champion_id, 10)
                const championName = champ?.name || ""
                const matchesSearch = !normalizedSearch || championName.toLowerCase().includes(normalizedSearch)
                const isSelected = championFilter === championId

                if (!matchesSearch && !isSelected) {
                    return null
                }

                return (
                    <button
                        ref={(node) => {
                            championFilterRefs.current[String(championId)] = node
                        }}
                        key={championId}
                        type="button"
                        className={getFilterButtonClass(isSelected)}
                        onClick={() => updateChampionFilterAndGetRecentMatchDetails(queueFilter, championId)}
                        onPointerUp={blurTappedButton}
                    >
                        {(championName || "").toUpperCase()}
                    </button>
                )
            }) || []),
        ].filter(Boolean)
    }

    function renderRoleFilterButtons() {
        return roleOptions.map((option) => {
            const isSelected = roleFilter === option.value

            return (
                <button
                    ref={(node) => {
                        roleFilterRefs.current[option.value || 'all'] = node
                    }}
                    key={option.label}
                    type="button"
                    className={`${getFilterButtonClass(isSelected)} inline-flex items-center gap-1.5`}
                    onClick={() => updateRoleFilterAndGetRecentMatchDetails(option.value)}
                    onPointerUp={blurTappedButton}
                >
                    {option.icon ? (
                        <img
                            alt=""
                            aria-hidden="true"
                            className="h-3.5 w-3.5 object-contain opacity-90"
                            src={`${process.env.PUBLIC_URL}/assets/role_icons/${option.icon}`}
                        />
                    ) : null}
                    {option.label}
                </button>
            )
        })
    }

    function renderFilterToolbar() {
        const queueFilterButtons = renderQueueFilterButtons()
        const championFilterButtons = renderChampionFilterButtons()
        const roleFilterButtons = renderRoleFilterButtons()
        const handleLayoutToggle = () => {
            setHideDesktopMatchCardChrome((currentValue) => {
                const nextValue = !currentValue
                localStorage.setItem(MATCH_HISTORY_LAYOUT_STORAGE_KEY, String(nextValue))
                return nextValue
            })
        }
        const handleFilterToolbarToggle = () => {
            setHideMatchFilters((currentValue) => {
                const nextValue = !currentValue
                localStorage.setItem(MATCH_FILTERS_COLLAPSED_STORAGE_KEY, String(nextValue))
                return nextValue
            })
        }

        return (
            <div className={`${filterToolbarClassName} ${hideMatchFilters ? '!pb-0' : ''}`}>
                <div className={`-mx-3 -mt-2.5 ${hideMatchFilters ? 'mb-0' : 'mb-3'} flex flex-wrap items-center justify-between gap-2 ${hideMatchFilters ? '' : 'border-b border-zinc-800'} bg-zinc-900/65 px-3 py-3`}>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Match Filters</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            aria-expanded={!hideMatchFilters}
                            type="button"
                            className={utilityButtonClass}
                            onClick={handleFilterToolbarToggle}
                            onPointerUp={blurTappedButton}
                        >
                            {hideMatchFilters ? 'Show Filters' : 'Hide Filters'}
                        </button>
                        <button
                            type="button"
                            className={utilityButtonClass}
                            onClick={() => undoAllFilters()}
                            onPointerUp={blurTappedButton}
                        >
                            Reset Filters
                        </button>
                        <button
                            aria-pressed={hideDesktopMatchCardChrome}
                            type="button"
                            className={`hidden lg:inline-flex ${utilityButtonClass}`}
                            onClick={handleLayoutToggle}
                            onPointerUp={blurTappedButton}
                        >
                            {hideDesktopMatchCardChrome ? 'Standard Layout' : 'Minimal Layout'}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="mt-3 flex min-h-[120px] items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900">
                        {renderLoadingSpinner()}
                    </div>
                ) : hideMatchFilters ? null : (
                    <div className="mt-3 grid gap-2.5 lg:grid-cols-[240px_minmax(0,1fr)]">
                        <div className={filterSectionCardClassName}>
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                <div className="min-w-0">
                                    <div className={filterGroupHeaderClassName}>
                                        <div className={filterSectionLabelClassName}>Queues</div>
                                    </div>
                                </div>
                                <div aria-hidden="true" className="hidden h-8 lg:block lg:w-[220px]" />
                            </div>
                            <div className="lg:hidden">
                                <HorizontalScrollArea
                                    className="mt-2"
                                    contentClassName="flex w-max items-center gap-1.5 pr-1"
                                    railClassName="mr-1 bg-zinc-900/88 group-hover:bg-zinc-800/82"
                                    thumbClassName="bg-zinc-800/85 group-hover:bg-zinc-600/90"
                                    viewportClassName="pr-1 pb-1"
                                >
                                    {queueFilterButtons}
                                </HorizontalScrollArea>
                            </div>
                            <div className="hidden lg:block">
                                <HorizontalScrollArea
                                    className="mt-2"
                                    contentClassName="inline-flex min-w-full flex-col gap-1.5"
                                    railClassName="mr-1 bg-zinc-900/88 group-hover:bg-zinc-800/82"
                                    thumbClassName="bg-zinc-800/85 group-hover:bg-zinc-600/90"
                                    viewportClassName="pr-1 pb-1"
                                >
                                    <div className="flex w-max items-center gap-1.5">
                                        {queueFilterButtons.filter((_, index) => index % 2 === 0)}
                                    </div>
                                    <div className="flex w-max items-center gap-1.5">
                                        {queueFilterButtons.filter((_, index) => index % 2 === 1)}
                                    </div>
                                </HorizontalScrollArea>
                            </div>
                        </div>

                        <div className={filterSectionCardClassName}>
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                <div className="min-w-0">
                                    <div className={filterGroupHeaderClassName}>
                                        <div className={filterSectionLabelClassName}>Champions</div>
                                    </div>
                                </div>
                                <div className="w-full lg:w-[220px]">
                                    <input
                                        className={filterSearchInputClassName}
                                        onChange={(event) => setChampionFilterSearch(event.target.value)}
                                        placeholder="CHAMPION SEARCH"
                                        type="text"
                                        value={championFilterSearch}
                                    />
                                </div>
                            </div>
                            <div className="lg:hidden">
                                <HorizontalScrollArea
                                    className="mt-2"
                                    contentClassName="flex w-max items-center gap-1.5 pr-1"
                                    railClassName="mr-1 bg-zinc-900/88 group-hover:bg-zinc-800/82"
                                    thumbClassName="bg-zinc-800/85 group-hover:bg-zinc-600/90"
                                    viewportClassName="pr-1 pb-1"
                                >
                                    {championFilterButtons}
                                </HorizontalScrollArea>
                            </div>
                            <div className="hidden lg:block">
                                <HorizontalScrollArea
                                    className="mt-2"
                                    contentClassName="inline-flex min-w-full flex-col gap-1.5"
                                    railClassName="mr-1 bg-zinc-900/88 group-hover:bg-zinc-800/82"
                                    thumbClassName="bg-zinc-800/85 group-hover:bg-zinc-600/90"
                                    viewportClassName="pr-1 pb-1"
                                >
                                    <div className="flex w-max items-center gap-1.5">
                                        {championFilterButtons.filter((_, index) => index % 2 === 0)}
                                    </div>
                                    <div className="flex w-max items-center gap-1.5">
                                        {championFilterButtons.filter((_, index) => index % 2 === 1)}
                                    </div>
                                </HorizontalScrollArea>
                            </div>
                        </div>

                        <div className={`${filterSectionCardClassName} lg:col-span-2`}>
                            <div className={filterGroupHeaderClassName}>
                                <div className={filterSectionLabelClassName}>Roles</div>
                            </div>
                            <div className="lg:hidden">
                                <HorizontalScrollArea
                                    className="mt-2"
                                    contentClassName="flex w-max items-center gap-1.5 pr-1"
                                    railClassName="mr-1 bg-zinc-900/88 group-hover:bg-zinc-800/82"
                                    thumbClassName="bg-zinc-800/85 group-hover:bg-zinc-600/90"
                                    viewportClassName="pr-1 pb-1"
                                >
                                    {roleFilterButtons}
                                </HorizontalScrollArea>
                            </div>
                            <div className="hidden lg:block">
                                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                    {roleFilterButtons}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-screen-lg pt-6 pb-4 sm:pt-8">
            <Seo
                title={seoTitle}
                description={seoDescription}
                canonical={isLiveGameRoute ? liveGamePath : matchHistoryPath}
                robots={isLiveGameRoute ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'ProfilePage',
                    name: seoTitle,
                    url: `https://winrates.gg${isLiveGameRoute ? liveGamePath : matchHistoryPath}`,
                    description: seoDescription,
                }}
            />
            <div className='mx-4 custom-lg:mx-0'>
                <div className="space-y-3 md:hidden">
                    {renderSummaryCard(true)}
                    {renderRankCard(displayedRank, true)}
                </div>
                <div className="hidden items-stretch gap-3 md:grid lg:hidden md:grid-cols-[320px_minmax(0,1fr)]">
                    {renderSummaryCard(false)}
                    {renderRankCard(displayedRank, true)}
                </div>
                <div className="hidden items-start gap-3 lg:grid lg:grid-cols-[320px_minmax(0,1fr)_minmax(0,1fr)]">
                    {renderSummaryCard(false)}
                    {renderRankCard("solo")}
                    {renderRankCard("flex")}
                </div>
                <div className='grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3 w-full mt-3'>
                    <div>
                        {loading ? renderLoadingCard('PREFERRED POSITION', 'min-h-[132px]', 'mb-3') : <PreferredRole preferredRole={preferredRole} />}
                        {loading ? renderLoadingCard('CHAMPION STATS', 'min-h-[168px]', 'mb-3') : <PersonalChampionPerformance personalChampionPerformances={personalChampionPerformances} />}
                        {loading ? renderLoadingCard('CHAMPION MASTERY', 'min-h-[220px]', 'mb-3') : <ChampionMastery championMastery={championMastery} />}
                        {loading ? renderLoadingCard('RECENTLY PLAYED WITH', 'min-h-[160px]', 'mt-3') : <RecentlyPlayedWith orderedChampions={orderedChampions} recentlyPlayedWith={recentlyPlayedWith} />}
                    </div>
                    <div ref={liveGamePanelRef} className='overflow-hidden scroll-mt-4'>
                        {isLiveGameRoute ? (
                            loading ? renderLoadingCard('LIVE GAME', 'min-h-[260px]') : (
                                <LiveGamePanel
                                    error={liveGameError}
                                    liveGameData={liveGameData}
                                    loading={liveGameLoading}
                                    onReturnToMatches={toggleLiveGamePanel}
                                    orderedChampions={orderedChampions}
                                    platformCode={summonerData?.platform_code}
                                    pollingStoppedReason={liveGamePollingStoppedReason}
                                    staleError={liveGameStaleError}
                                />
                            )
                        ) : (
                            <>
                                {renderFilterToolbar()}
                                <div className='block lg:hidden'>
                                    {loading ? renderLoadingCard('MATCH HISTORY', 'min-h-[340px]', 'mb-3', 'h-10') : <MatchHistory orderedChampions={orderedChampions} matchHistory={matchHistory} setSummonerData={setSummonerData} summonerData={summonerData} />}
                                </div>
                                <div className='hidden lg:block'>
                                    {loading ? renderLoadingCard('MATCH HISTORY', 'min-h-[360px]', 'mb-3', 'h-10') : <MatchHistoryDesktop hideCardChrome={hideDesktopMatchCardChrome} orderedChampions={orderedChampions} colorToggle={1} matchHistory={matchHistory} setSummonerData={setSummonerData} summonerData={summonerData} />}
                                </div>
                                <div className={`space-y-2 ${!matchHistory?.length ? 'mt-3' : ''}`}>
                                    {matchHistoryStatusMessage ? (
                                        <div className="text-center text-[11px] italic tracking-[0.02em] text-zinc-400">
                                            {matchHistoryStatusMessage}
                                        </div>
                                    ) : null}
                                    <button
                                        type="button"
                                        className={showMoreButtonClass}
                                        disabled={loading}
                                        onClick={loadMoreMatches}
                                    >
                                        SHOW MORE GAMES
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SummonerDetail
